/**
 * Feature Flags Configuration
 *
 * These flags control new features and UI changes during phased rollouts.
 * All flags are opt-in and additive (no removals during rollout).
 */

// =============================================================================
// HELPER: Parse environment variable with multiple truthy/falsy values
// =============================================================================
function parseEnvFlag(value: string | undefined): boolean {
  if (!value) return false
  const normalized = value.toLowerCase().trim()
  return normalized === "1" || normalized === "true" || normalized === "on"
}

// =============================================================================
// MVP GATING FLAGS (P0 - MILESTONE A)
// =============================================================================
// These flags control access to non-MVP surfaces during production validation

// Dashboard access: Hide dashboard nav item and prevent direct URL access
// Default: FALSE in production, TRUE in staging/dev
// Client-side: controls navigation visibility
export const FF_DASHBOARD_ENABLED =
  parseEnvFlag(process.env.NEXT_PUBLIC_FEATURE_DASHBOARD) ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// Legacy navigation items: Show/hide non-MVP nav sections
// Default: FALSE in production, TRUE in staging/dev
// Client-side: controls navigation structure
export const FF_LEGACY_NAV_ENABLED =
  parseEnvFlag(process.env.NEXT_PUBLIC_FEATURE_LEGACY_NAV) ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// Dev tools access: Enable developer-only features and debugging tools
// Default: FALSE in production, TRUE in staging/dev
// Client-side: controls dev tool visibility
export const FF_DEVTOOLS_ENABLED =
  parseEnvFlag(process.env.NEXT_PUBLIC_FEATURE_DEVTOOLS) ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// Memory Proposals: Enable inline memory suggestion cards in Advisor chat
// Default: TRUE in dev/staging, FALSE in production unless explicitly enabled
// Client-side: controls memory proposal card rendering and actions
export const FF_MEMORY_PROPOSALS_ENABLED =
  process.env.NEXT_PUBLIC_FF_MEMORY_PROPOSALS === "true" ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// =============================================================================
// AXIO INTEGRATION FLAGS (LOGOS-CORE-003)
// =============================================================================
// These flags control the phased rollout of Axio as the canonical Human
// Interaction Layer (HIL) per Core System Document LOGOS-CORE-003 v1.1

// Master switch: Enable Axio integration system-wide
export const AXIO_ENABLED = process.env.NEXT_PUBLIC_AXIO_ENABLED === "true"

// Apply Axio voice contract to advisor system prompt
// Server-only: does not need NEXT_PUBLIC prefix
export const AXIO_PROMPT_ENABLED = process.env.AXIO_PROMPT_ENABLED === "true"

// Switch UI copy to use Axio language ("Axio will remember this")
export const AXIO_UI_COPY_ENABLED = process.env.NEXT_PUBLIC_AXIO_UI_COPY_ENABLED === "true"

// Enable Axio compliance gate (pre-delivery response checking)
// Server-only: does not need NEXT_PUBLIC prefix
export const AXIO_COMPLIANCE_GATE_ENABLED = process.env.AXIO_COMPLIANCE_GATE_ENABLED === "true"

// =============================================================================
// AGS INTEGRATION FLAGS (LOGOS-CORE-004)
// =============================================================================
// These flags control the phased rollout of the Action Guidance Stack (AGS)
// per Core System Document LOGOS-CORE-004 v1.2

// PM DIRECTIVE (DEC 2024): AGS disabled pending MVP validation
// These flags control the phased rollout of the Action Guidance Stack (AGS)
// per Core System Document LOGOS-CORE-004 v1.2
//
// STATUS: DISABLED BY DEFAULT - NOT REQUIRED FOR MVP VALIDATION
// Violates "No new canonical systems" rule per MVP Alignment Roadmap v1.0
// AGS will be revisited post-validation if memory advantage is proven

// Master switch: Enable AGS goals/constraints reading from Business Brain
// Default: FALSE in all environments until MVP validation complete
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_GOALS_ENABLED = process.env.AGS_GOALS_ENABLED === "true"

// Enable AGS constraints-first precheck gate
// Default: FALSE in all environments until MVP validation complete
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_CONSTRAINTS_ENABLED = process.env.AGS_CONSTRAINTS_ENABLED === "true"

// Enable AGS evidence citation requirements
// Default: FALSE in all environments until MVP validation complete
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_EVIDENCE_CITATIONS = process.env.AGS_EVIDENCE_CITATIONS === "true"

// Enable AGS strategy workflow (already exists if present)
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_STRATEGY_WORKFLOW = process.env.AGS_STRATEGY_WORKFLOW === "true"

// Enable AGS manual review loop trigger
// Default: FALSE in all environments until MVP validation complete
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_REVIEW_LOOP = process.env.AGS_REVIEW_LOOP === "true"

// Enable AGS governance gates (goal-link check, etc.)
// Default: FALSE in all environments until MVP validation complete
// Server-only: does not need NEXT_PUBLIC prefix
export const AGS_GOVERNANCE_GATES = process.env.AGS_GOVERNANCE_GATES === "true"

// Client-side flags for UI display
export const AGS_SHOW_GOALS = process.env.NEXT_PUBLIC_AGS_SHOW_GOALS === "true"
export const AGS_SHOW_REVIEW = process.env.NEXT_PUBLIC_AGS_SHOW_REVIEW === "true"

// =============================================================================
// THINKINGVISUAL FLAGS
// =============================================================================
// PM DIRECTIVE (DEC 2024): ThinkingVisual disabled by default pending MVP validation
// Visual polish does not validate core hypothesis
// Adds bundle weight and cognitive noise
//
// STATUS: DISABLED BY DEFAULT - NOT REQUIRED FOR MVP VALIDATION
// Will be revisited post-validation if proven valuable for activation

// Enable ThinkingVisual breathing animations in chat/login
// Default: FALSE - visual polish not required for MVP validation
export const FF_THINKING_VISUAL_ENABLED = process.env.NEXT_PUBLIC_FF_THINKING_VISUAL_ENABLED === "true"

// =============================================================================
// CITATIONS UI FLAGS (MILESTONE B)
// =============================================================================
// These flags control the phased rollout of the citation/linking system
// per P1 directive: Improve trust + activation via clear citations

// Master switch: Enable structured citations UI in Advisor chat
// Default: TRUE in dev/staging, FALSE in production until validated
// Client-side: controls citation rendering in messages
export const FF_CITATIONS_UI_ENABLED =
  process.env.NEXT_PUBLIC_FF_CITATIONS_UI === "true" ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// Placeholder mode: Enable design-phase citation pills without real source metadata
// Default: FALSE in production, TRUE in dev/staging for design visualization
// Client-side: controls whether pills show placeholder tooltips and are non-clickable
export const FF_CITATIONS_PLACEHOLDER_MODE =
  process.env.NEXT_PUBLIC_FF_CITATIONS_PLACEHOLDER === "true" ||
  (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" && process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined)

// =============================================================================
// EXISTING FEATURE FLAGS
// =============================================================================
// Always-Advisor Mode: Makes advisor chat the default experience
export const FF_ALWAYS_ADVISOR_MODE = process.env.NEXT_PUBLIC_FF_ALWAYS_ADVISOR_MODE === "true"

// Business Brain Builder: Replaces right panel with familiar UI patterns
export const FF_BUSINESS_BRAIN_BUILDER = process.env.NEXT_PUBLIC_FF_BUSINESS_BRAIN_BUILDER === "true"

// Progressive Disclosure: Enforces "Rule of 3" in UI
export const FF_PROGRESSIVE_DISCLOSURE = process.env.NEXT_PUBLIC_FF_PROGRESSIVE_DISCLOSURE === "true"

// Streaming Responses: Enable progressive token-by-token display in chat
// Default: false (reliability first, streaming later per P0 directive)
export const FF_ENABLE_STREAMING = process.env.NEXT_PUBLIC_FF_ENABLE_STREAMING === "true"

/**
 * Helper to check if feature flags are enabled on server
 * Use this for server-side conditional logic
 */
export function getServerFeatureFlags() {
  return {
    dashboardEnabled: FF_DASHBOARD_ENABLED,
    legacyNavEnabled: FF_LEGACY_NAV_ENABLED,
    devToolsEnabled: FF_DEVTOOLS_ENABLED,
    alwaysAdvisorMode: FF_ALWAYS_ADVISOR_MODE,
    businessBrainBuilder: FF_BUSINESS_BRAIN_BUILDER,
    progressiveDisclosure: FF_PROGRESSIVE_DISCLOSURE,
    enableStreaming: FF_ENABLE_STREAMING,
    memoryProposalsEnabled: FF_MEMORY_PROPOSALS_ENABLED,
    axioEnabled: AXIO_ENABLED,
    axioPromptEnabled: AXIO_PROMPT_ENABLED,
    axioUiCopyEnabled: AXIO_UI_COPY_ENABLED,
    axioComplianceGateEnabled: AXIO_COMPLIANCE_GATE_ENABLED,
    agsGoalsEnabled: AGS_GOALS_ENABLED,
    agsConstraintsEnabled: AGS_CONSTRAINTS_ENABLED,
    agsEvidenceCitations: AGS_EVIDENCE_CITATIONS,
    agsStrategyWorkflow: AGS_STRATEGY_WORKFLOW,
    agsReviewLoop: AGS_REVIEW_LOOP,
    agsGovernanceGates: AGS_GOVERNANCE_GATES,
    agsShowGoals: AGS_SHOW_GOALS,
    agsShowReview: AGS_SHOW_REVIEW,
    thinkingVisualEnabled: FF_THINKING_VISUAL_ENABLED,
    citationsUiEnabled: FF_CITATIONS_UI_ENABLED,
    citationsPlaceholderMode: FF_CITATIONS_PLACEHOLDER_MODE,
  }
}

/**
 * Check if a specific feature flag is enabled
 * Use this for conditional logic throughout the app
 */
export function isFeatureEnabled(flag: keyof ReturnType<typeof getServerFeatureFlags>): boolean {
  const flags = {
    dashboardEnabled: FF_DASHBOARD_ENABLED,
    legacyNavEnabled: FF_LEGACY_NAV_ENABLED,
    devToolsEnabled: FF_DEVTOOLS_ENABLED,
    alwaysAdvisorMode: FF_ALWAYS_ADVISOR_MODE,
    businessBrainBuilder: FF_BUSINESS_BRAIN_BUILDER,
    progressiveDisclosure: FF_PROGRESSIVE_DISCLOSURE,
    enableStreaming: FF_ENABLE_STREAMING,
    memoryProposalsEnabled: FF_MEMORY_PROPOSALS_ENABLED,
    axioEnabled: AXIO_ENABLED,
    axioPromptEnabled: AXIO_PROMPT_ENABLED,
    axioUiCopyEnabled: AXIO_UI_COPY_ENABLED,
    axioComplianceGateEnabled: AXIO_COMPLIANCE_GATE_ENABLED,
    agsGoalsEnabled: AGS_GOALS_ENABLED,
    agsConstraintsEnabled: AGS_CONSTRAINTS_ENABLED,
    agsEvidenceCitations: AGS_EVIDENCE_CITATIONS,
    agsStrategyWorkflow: AGS_STRATEGY_WORKFLOW,
    agsReviewLoop: AGS_REVIEW_LOOP,
    agsGovernanceGates: AGS_GOVERNANCE_GATES,
    agsShowGoals: AGS_SHOW_GOALS,
    agsShowReview: AGS_SHOW_REVIEW,
    thinkingVisualEnabled: FF_THINKING_VISUAL_ENABLED,
    citationsUiEnabled: FF_CITATIONS_UI_ENABLED,
    citationsPlaceholderMode: FF_CITATIONS_PLACEHOLDER_MODE,
  }
  return flags[flag] || false
}

/**
 * Get list of all enabled feature flags
 * Useful for debugging and analytics
 */
export function getEnabledFeatures(): string[] {
  const flags = {
    FF_DASHBOARD_ENABLED,
    FF_LEGACY_NAV_ENABLED,
    FF_DEVTOOLS_ENABLED,
    FF_ALWAYS_ADVISOR_MODE,
    FF_BUSINESS_BRAIN_BUILDER,
    FF_PROGRESSIVE_DISCLOSURE,
    FF_ENABLE_STREAMING,
    FF_MEMORY_PROPOSALS_ENABLED,
    AXIO_ENABLED,
    AXIO_PROMPT_ENABLED,
    AXIO_UI_COPY_ENABLED,
    AXIO_COMPLIANCE_GATE_ENABLED,
    AGS_GOALS_ENABLED,
    AGS_CONSTRAINTS_ENABLED,
    AGS_EVIDENCE_CITATIONS,
    AGS_STRATEGY_WORKFLOW,
    AGS_REVIEW_LOOP,
    AGS_GOVERNANCE_GATES,
    AGS_SHOW_GOALS,
    AGS_SHOW_REVIEW,
    FF_THINKING_VISUAL_ENABLED,
    FF_CITATIONS_UI_ENABLED,
    FF_CITATIONS_PLACEHOLDER_MODE,
  }
  return Object.entries(flags)
    .filter(([_, enabled]) => enabled)
    .map(([name]) => name)
}
